import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { AppSettingsService, DeferLoadDirective, FeatureService } from '@woolworthsnz/styleguide';
import { ContextResponse, ProductImagesResponse } from '@woolworthsnz/trader-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import EnabledFeaturesEnum = ContextResponse.EnabledFeaturesEnum;

interface PictureSource {
	srcset: string;
	media: string | null;
}

type Size = 'thumb' | 'small' | 'big' | 'zoom' | 'extra-small' | 'extra-extra-small';
type ImageIndexKey = keyof ProductImagesResponse;

const SmallImageIndexes: Record<Size, ImageIndexKey> = {
	thumb: 'small',
	small: 'small',
	big: 'big',
	zoom: 'big',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'extra-small': 'big',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'extra-extra-small': 'big',
};

@Component({
	selector: 'product-image, [cdxProductImage]',
	template: `
		<ng-container *ngIf="isLazyLoadingFeatureEnabled$ | async; else fallback">
			<picture>
				<ng-container *ngIf="!imageErrored">
					<source
						*ngFor="let source of sources$ | async"
						[attr.media]="source.media"
						[srcset]="source.srcset"
					/>
				</ng-container>
				<img
					[src]="src"
					loading="lazy"
					(error)="handleImageError()"
					[attr.aria-labelledby]="labelledBy"
					[alt]="name"
				/>
			</picture>
		</ng-container>

		<ng-template #fallback>
			<img
				deferLoad
				[attr.aria-labelledby]="labelledBy"
				[fallback]="getImageUrl('default')"
				[sizes]="sizes"
				[src]="src"
				[srcset]="srcset"
				[alt]="name"
			/>
		</ng-template>
	`,
	styleUrls: ['./image.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgFor, DeferLoadDirective, AsyncPipe],
})
export class ProductImageComponent implements OnInit {
	@Input() labelledBy: string;
	@Input() name: string;
	@HostBinding('attr.size')
	@Input()
	size: Size = 'big';
	@Input()
	lazyLoad = false;
	isLazyLoadingFeatureEnabled$: Observable<boolean>;
	sources$ = new BehaviorSubject<PictureSource[]>([]);
	imageErrored = false;
	private productImages: ProductImagesResponse = {};

	constructor(private appSettingsService: AppSettingsService, private featureService: FeatureService) {}

	get src(): string {
		if (this.imageErrored) {
			return this.getImageUrl('default');
		}
		return (this.images && this.images.big) || this.getImageUrl('default');
	}
	get srcset(): string | null {
		if (!this.images) {
			return null;
		}
		return `${this.images[SmallImageIndexes[this.size]]}, ${this.images.big}, ${this.images.big}`;
	}

	get sizes(): string | null {
		if (!this.images) {
			return null;
		}

		const breakpoints = this.appSettingsService.getBreakpoints();

		return `(min-width: ${breakpoints.mobile}), (min-width: ${breakpoints['tablet-portrait']}), (min-width: ${breakpoints.tablet}), (min-width: ${breakpoints.desktop}), (min-width: ${breakpoints.large}) (max-width: ${breakpoints.xlarge})`;
	}

	get images(): ProductImagesResponse {
		return this.productImages;
	}
	@Input() set images(value: ProductImagesResponse) {
		this.productImages = value;
		this.updateSources();
	}

	ngOnInit(): void {
		this.isLazyLoadingFeatureEnabled$ = this.featureService.state$.pipe(
			map(
				(featureState) =>
					this.lazyLoad &&
					this.isLazyLoadingSupported() &&
					featureState.enabledFeatures.includes(EnabledFeaturesEnum.LazyLoadProductStampImages)
			)
		);
	}

	getImageUrl(type: string): string {
		return `/images/product_${type}.png`;
	}

	handleImageError(): void {
		this.imageErrored = true;
	}

	updateSources(): void {
		const sources: PictureSource[] = [];
		const breakpoints = this.appSettingsService.getBreakpoints();

		if (!this.images) {
			this.sources$.next(sources);
			return;
		}

		// We currently always use the 'big' image. Aim is that Akamai image manager does the optimisation
		// Small images are a left over from a different image management strategy
		// Leaving the `sources` support in case we do want to use <picture> properly in future
		if (this.images.big) {
			sources.push({
				media: `(min-width: ${breakpoints.mobile})`,
				srcset: this.images.big,
			});
		}

		this.sources$.next(sources);
	}

	isLazyLoadingSupported(): boolean {
		return 'loading' in HTMLImageElement.prototype;
	}
}
